import { CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Input,
  Modal,
  Radio,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import CommonTable from '../../../components/CommonTable';

const BEHAVIOUR_LABELS = {
  leave: 'Unplanned Leave',
  wfh_over: 'WFH Over-limit',
  hours_over: 'Hours Over-limit',
  late: 'Coming Late',
  early: 'Leaving Early',
  standup: 'Missed Standup',
  xbrk: 'Extended Break',
  sbrk: 'Short Break',
};

const BULK_ACTION_LABELS = {
  excuse: { title: 'Update Genuine Status — Selected Incidents', confirm: 'Save' },
  archive: { title: 'Update Archive Status — Selected Incidents', confirm: 'Save' },
};

/**
 * IncidentHistoryDrawer shows one employee's full-year incident history,
 * sourced from the attendanceIncidents query. The view icon opens the
 * Manage Incident modal, same as the Incidents tab; its Excuse/Archive tabs
 * are only editable for users with update permission (canUpdate). Rows can
 * also be multi-selected to mark genuine or archive several incidents at
 * once via onBulkAction, instead of opening the Manage Incident modal for
 * each one individually.
 */
function IncidentHistoryDrawer({
  open,
  loading,
  employeeName,
  data,
  onClose,
  onEdit,
  canUpdate,
  currentUserId,
  onChange,
  paginationConfig,
  onBulkAction,
  bulkLoading,
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // 'excuse' | 'archive' | null
  const [bulkReason, setBulkReason] = useState('');
  // Only relevant for the 'excuse' action — reason is required when turning
  // Genuine off (isExcused: false), optional when turning it on.
  const [bulkIsExcused, setBulkIsExcused] = useState(true);
  // Only relevant for the 'archive' action — reason is required when
  // archiving (isArchived: true), not collected when unarchiving.
  const [bulkIsArchived, setBulkIsArchived] = useState(true);

  const isOwnIncident = (record) =>
    !!currentUserId && record?.employee?.id === currentUserId;

  const isBulkReasonRequired =
    (bulkAction === 'archive' && bulkIsArchived) ||
    (bulkAction === 'excuse' && !bulkIsExcused);

  const openBulkModal = (action) => {
    setBulkReason('');
    setBulkIsExcused(true);
    setBulkIsArchived(true);
    setBulkAction(action);
  };

  const handleBulkConfirm = async () => {
    if (isBulkReasonRequired && !bulkReason.trim()) return;
    try {
      await onBulkAction({
        action: bulkAction,
        ids: selectedRowKeys,
        isExcused: bulkIsExcused,
        isArchived: bulkIsArchived,
        reason: bulkReason.trim() || undefined,
      });
      setSelectedRowKeys([]);
      setBulkAction(null);
      setBulkReason('');
    } catch {
      // onBulkAction already surfaces a toast for whichever incidents failed.
    }
  };

  const rowSelection = canUpdate
    ? {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        getCheckboxProps: (record) => ({ disabled: isOwnIncident(record) }),
      }
    : undefined;

  const columns = [
    {
      title: 'Incident Time',
      key: 'loggedAt',
      render: (_, record) =>
        record.loggedAt
          ? dayjs(record.loggedAt).format('DD MMM YYYY, hh:mm A')
          : '—',
    },
    {
      title: 'Behaviour',
      key: 'behaviourKey',
      render: (_, record) =>
        BEHAVIOUR_LABELS[record.behaviourKey] || record.behaviourKey,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => value || '—',
    },
    {
      title: 'Count',
      dataIndex: 'scoreImpact',
      key: 'scoreImpact',
      align: 'center',
      render: (value) => value || '—',
    },
    {
      title: 'Genuine',
      key: 'isExcused',
      align: 'center',
      render: (_, record) =>
        record.excusedCount > 0 ? (
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        ) : (
          '—'
        ),
    },
    {
      title: 'Status',
      key: 'isArchived',
      align: 'center',
      render: (_, record) =>
        record.isArchived ? (
          <Tag color="default">Archived</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        // A user cannot edit their own incident, even with update permission.
        const canEditRecord = canUpdate && !isOwnIncident(record);
        return (
          <Tooltip title={canEditRecord ? 'View / Edit' : 'View'}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onEdit && onEdit(record)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Drawer
      title={
        <span>
          {employeeName
            ? `${employeeName} — Incident History`
            : 'Incident History'}
        </span>
      }
      open={open}
      onClose={onClose}
      width={760}
      destroyOnClose
    >
      {canUpdate && selectedRowKeys.length > 0 && (
        <div className="incident-history-bulk-bar">
          <span>{selectedRowKeys.length} selected</span>
          <Space>
            <Button onClick={() => openBulkModal('excuse')}>Genuine</Button>
            <Button onClick={() => openBulkModal('archive')}>Archive</Button>
            <Button type="link" onClick={() => setSelectedRowKeys([])}>
              Clear
            </Button>
          </Space>
        </div>
      )}
      <CommonTable
        size="small"
        bordered
        loadingData={loading}
        columns={columns}
        data={data || []}
        rowKey={(obj) => obj?.id}
        onChange={onChange}
        paginationConfig={paginationConfig}
        rowSelection={rowSelection}
      />
      <Modal
        title={BULK_ACTION_LABELS[bulkAction]?.title}
        open={!!bulkAction}
        onCancel={() => setBulkAction(null)}
        onOk={handleBulkConfirm}
        confirmLoading={bulkLoading}
        okText={BULK_ACTION_LABELS[bulkAction]?.confirm}
        okButtonProps={{
          disabled: isBulkReasonRequired && !bulkReason.trim(),
        }}
        destroyOnClose
      >
        <p>{selectedRowKeys.length} incident(s) selected.</p>
        {bulkAction === 'excuse' && (
          <Radio.Group
            value={bulkIsExcused}
            onChange={(e) => setBulkIsExcused(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio value>Mark Genuine</Radio>
            <Radio value={false}>Remove Genuine</Radio>
          </Radio.Group>
        )}
        {bulkAction === 'archive' && (
          <Radio.Group
            value={bulkIsArchived}
            onChange={(e) => {
              setBulkIsArchived(e.target.value);
              // Unarchiving doesn't collect a reason — clear any typed value
              // so a stale reason from a prior "Archive" isn't submitted
              // once the field is hidden.
              if (!e.target.value) setBulkReason('');
            }}
            style={{ marginBottom: 16 }}
          >
            <Radio value>Archive</Radio>
            <Radio value={false}>Unarchive</Radio>
          </Radio.Group>
        )}
        {(bulkAction !== 'archive' || bulkIsArchived) && (
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder={
              isBulkReasonRequired
                ? `Enter reason for ${
                    bulkAction === 'archive' ? 'archiving' : 'removing genuine'
                  }`
                : 'Enter reason (optional)'
            }
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
          />
        )}
      </Modal>
    </Drawer>
  );
}

export default IncidentHistoryDrawer;
